// Session slot timing — HANDOFF schema Section 8.
//
// Given wake (W) and sleep (S) times in the participant's local time:
//   Slot 1: W
//   Slot 2: W + round((S - W) * 0.25)
//   Slot 3: W + round((S - W) * 0.50)
//   Slot 4: W + round((S - W) * 0.75)
//   Slot 5: S - 30 minutes
// If the W→S gap is under 4 hours, fall back to fixed offsets:
//   W, W+1h, W+2h, W+3h, S-30min
//
// Must stay in step with worker/lib/schedule.js, which is what actually sends
// the reminders — this copy only drives the "Next reminder around…" line on
// the Dashboard, and the two disagreeing would be worse than either being
// wrong on its own.

function toMinutes(hour, minute) {
  return hour * 60 + minute;
}

function fromMinutes(totalMinutes) {
  const m = ((totalMinutes % 1440) + 1440) % 1440;
  return { hour: Math.floor(m / 60), minute: m % 60 };
}

export function computeSessionSlots(schedule) {
  if (!schedule) return [];
  const { wake_hour, wake_minute, sleep_hour, sleep_minute } = schedule;
  const w = toMinutes(wake_hour, wake_minute);
  let s = toMinutes(sleep_hour, sleep_minute);
  if (s <= w) s += 1440; // sleep time is "past midnight" relative to wake

  const gap = s - w;
  let offsets;
  if (gap < 240) {
    // Fallback: fixed offsets when the waking window is too short for 5
    // proportionally-spaced sessions to make sense.
    offsets = [0, 60, 120, 180, gap - 30];
  } else {
    // Last slot 30 minutes before bedtime. Its final reminder therefore lands
    // at roughly the stated sleep time, which is deliberate.
    offsets = [0, Math.round(gap * 0.25), Math.round(gap * 0.5), Math.round(gap * 0.75), gap - 30];
  }

  return offsets.map((offset, i) => {
    const { hour, minute } = fromMinutes(w + offset);
    return { slot: i + 1, hour, minute };
  });
}

/** Returns the next upcoming slot (today or tomorrow) as a Date, plus its slot number. */
export function nextSessionTime(schedule, now = new Date()) {
  const slots = computeSessionSlots(schedule);
  if (slots.length === 0) return null;

  const candidates = [];
  for (const offsetDays of [0, 1]) {
    for (const { slot, hour, minute } of slots) {
      const d = new Date(now);
      d.setDate(d.getDate() + offsetDays);
      d.setHours(hour, minute, 0, 0);
      if (d > now) candidates.push({ date: d, slot });
    }
  }
  candidates.sort((a, b) => a.date - b.date);
  return candidates[0] ?? null;
}

export function formatTime(hour, minute) {
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  const ampm = hour < 12 ? 'AM' : 'PM';
  return `${h12}:${String(minute).padStart(2, '0')} ${ampm}`;
}

/**
 * True if the sleep time, read on a plain 24-hour clock, falls at or before
 * the wake time -- meaning it's actually the following calendar day (e.g.
 * wake 8:00 AM, sleep 1:30 AM really means 1:30 AM the NEXT morning, not
 * "1:30 AM today", which already happened). computeSessionSlots() already
 * handles this correctly in its math (`if (s <= w) s += 1440`); this just
 * exposes the same check for the UI to show a clarifying note, since a
 * plain 24-hour dropdown gives no visual indication of the day rollover on
 * its own -- easy to select without realizing what it actually means.
 */
export function isSleepNextDay(wakeHour, wakeMinute, sleepHour, sleepMinute) {
  return sleepHour * 60 + sleepMinute <= wakeHour * 60 + wakeMinute;
}
